import './modelselector.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Alert, Modal } from 'react-bootstrap';
import { AutoForm } from 'uniforms-bootstrap5';
import { createSchemaBridge } from '../../utils/uniformsHelper';
import { useTranslation } from 'react-i18next';
import WeatherDataSelector from '../WeatherDataSelector/WeatherDataSelector';
import { Check2Circle } from 'react-bootstrap-icons';
import { WeatherParameter } from '../../Api/Interfaces/Interfaces';

const ModelSelector = ({ selectedCrop, parcelPosition, onModelConfigured, onEditingChange = () => { } }) => {
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showWeatherConfig, setShowWeatherConfig] = useState(false);
    const [showParametersConfig, setShowParametersConfig] = useState(false);
    const [schemaBridge, setSchemaBridge] = useState(null);
    const [showDescription, setShowDescription] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [modelConfigurations, setModelConfigurations] = useState({});
    const [weatherParameters, setWeatherParameters] = useState([]);
    const formRef = useRef(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (!selectedCrop) return;

        setLoading(true);
        setError(null);
        try {
            // Utilizziamo direttamente i modelli dal CropDto
            const cropModels = selectedCrop.models || [];
            setModels(cropModels);
            setLoading(false);
        } catch (err) {
            setError(t("error:loadModelsFailed"));
            setLoading(false);
        }
    }, [selectedCrop]);

    const handleModelSelect = async (model) => {
        try {
            if (selectedModel?.id === model.id) {
                // Deseleziona il modello e rimuovi la sua configurazione
                setSelectedModel(null);
                setSchemaBridge(null);
                setShowWeatherConfig(false);
                setShowParametersConfig(false);
                // Rimuovi la configurazione del modello deselezionato
                setModelConfigurations(prev => {
                    const newConfig = { ...prev };
                    delete newConfig[model.id];
                    return newConfig;
                });
                onModelConfigured([]);
            } else {
                // Chiudi tutti i pannelli quando cambi modello
                setShowWeatherConfig(false);
                setShowParametersConfig(false);
                setSelectedModel(model);

                // Estraiamo i WeatherParameters dal dslContext
                if (model.dslContext?.['@context']?.params?.values) {
                    const parameters = model.dslContext['@context'].params.values
                        .map(value => {
                            if (value['@value']) {
                                return value['@value'];
                            } else if (value['@id']) {
                                const match = value['@id'].match(/hl:(\w+)/);
                                if (!match) return null;
                                const enumKey = match[1];
                                return WeatherParameter[enumKey];
                            }
                            return null;
                        })
                        .filter(Boolean);
                    setWeatherParameters(parameters);
                } else {
                    setWeatherParameters([]);
                }

                if (model.jsonSchema) {
                    const bridge = createSchemaBridge(model.jsonSchema);
                    setSchemaBridge(bridge);
                } else {
                    setSchemaBridge(null);
                }
                // Non resettare più la configurazione quando si seleziona un nuovo modello
                // La configurazione verrà sovrascritta solo quando l'utente imposta nuovi valori
            }
        } catch (err) {
            setError(t("error:selectModelFailed"));
        }
    };

    // Funzione per verificare se un modello è configurato correttamente
    const isModelConfigured = useCallback((modelId) => {
        const config = modelConfigurations[modelId];
        const model = models.find(m => m.id === modelId);
        if (!config || !model) return false;

        // Un modello è configurato se:
        // 1. Ha una configurazione meteo valida (deve avere una stazione selezionata)
        const hasValidWeatherConfig = config.weatherConfig?.station;

        // 2. Se ha jsonSchema, deve avere anche i parametri configurati
        // 3. Se non ha jsonSchema, è valido anche senza parametri
        const hasValidParameters = !model.jsonSchema ||
            (config.parameters && Object.keys(config.parameters).length > 0);

        return hasValidWeatherConfig && hasValidParameters;
    }, [modelConfigurations, models]);

    // Memorizziamo updateModelConfigurations con useCallback per evitare ricreazioni inutili
    const updateModelConfigurationsCallback = useCallback(() => {
        // Non notifichiamo configurazioni durante l'editing
        if (showWeatherConfig || showParametersConfig) {
            onModelConfigured([]);
            return;
        }

        const configuredModels = Object.entries(modelConfigurations)
            .filter(([modelId]) => isModelConfigured(modelId))
            .map(([modelId, config]) => ({
                modelId,
                ...config
            }));

        onModelConfigured(configuredModels);
    }, [modelConfigurations, showWeatherConfig, showParametersConfig, onModelConfigured, isModelConfigured]);

    // Monitoriamo lo stato di editing e notifichiamo il componente padre
    useEffect(() => {
        const isEditing = showWeatherConfig || showParametersConfig;
        onEditingChange(isEditing);

        // Se non stiamo editando, aggiorniamo le configurazioni
        if (!isEditing) {
            updateModelConfigurationsCallback();
        }
    }, [showWeatherConfig, showParametersConfig, onEditingChange]);

    const handleWeatherConfigSubmit = (weatherConfig) => {
        if (!selectedModel) return;

        setModelConfigurations(prev => {
            const newConfig = {
                ...prev,
                [selectedModel.id]: {
                    ...prev[selectedModel.id],
                    weatherConfig
                }
            };
            return newConfig;
        });
        setShowWeatherConfig(false);
    };

    const handleParametersSubmit = (parameters) => {
        if (!selectedModel) return;

        setModelConfigurations(prev => ({
            ...prev,
            [selectedModel.id]: {
                ...prev[selectedModel.id],
                parameters
            }
        }));
        setShowParametersConfig(false);
        updateModelConfigurations();
    };

    // Usiamo la versione memorizzata di updateModelConfigurations
    const updateModelConfigurations = updateModelConfigurationsCallback;

    const handleShowDescription = (e, description) => {
        e.stopPropagation();
        setSelectedDescription(description);
        setShowDescription(true);
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="model-selector">
            <div className="model-selector-header">
                <h5 className="text-muted">
                    {t("modelselector:messages.infoPlatform")}
                </h5>
            </div>

            <div className="model-selector-content">
                <div className="models-list-container">
                    {loading && <div className="text-center py-3">{t("modelselector:messages.loadingModels")}</div>}
                    {error && <Alert variant="danger">{error}</Alert>}

                    <div className="models-vertical-scroll">
                        {models.map((model) => (
                            <Card
                                key={model.id}
                                className={`model-card ${selectedModel?.id === model.id ? 'selected' : ''}`}
                                onClick={() => handleModelSelect(model)}
                            >
                                <Card.Body className="p-0">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <h5 className="model-name mb-0">
                                                {model.name}
                                            </h5>
                                            {isModelConfigured(model.id) && (
                                                <Check2Circle className="text-success" size={20} />
                                            )}
                                            {selectedModel?.id === model.id && (
                                                <div className="selected-indicator" />
                                            )}
                                        </div>
                                    </div>
                                    <Card.Text
                                        className="description-link"
                                        onClick={(e) => handleShowDescription(e, model.description)}
                                    >
                                        <div dangerouslySetInnerHTML={{ __html: truncateText(model.description) }} />
                                        <span className="text-primary ms-1">[{t("modelselector:messages.moreInfo")}]</span>
                                    </Card.Text>
                                    {selectedModel?.id === model.id && (
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant={modelConfigurations[selectedModel.id]?.weatherConfig ? "success" : "outline-primary"}
                                                className="w-50 d-flex align-items-center justify-content-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowParametersConfig(false); // Chiudi il pannello parametri
                                                    setShowWeatherConfig(true);
                                                    onEditingChange(true);
                                                }}
                                            >
                                                {t("modelselector:buttons.weatherData")}
                                            </Button>
                                            <Button
                                                variant={modelConfigurations[selectedModel.id]?.parameters ? "success" : "outline-primary"}
                                                className="w-50 d-flex align-items-center justify-content-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowWeatherConfig(false); // Chiudi il pannello weather
                                                    setShowParametersConfig(true);
                                                    onEditingChange(true);
                                                }}
                                                disabled={!selectedModel}
                                            >
                                                {t("modelselector:buttons.parameters")}
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                </div>

                <div className="configuration-panel">
                    {showWeatherConfig && selectedModel && (
                        <div className="weather-config-container">
                            <WeatherDataSelector
                                variables={weatherParameters}
                                latitude={parcelPosition?.lat}
                                longitude={parcelPosition?.lon}
                                onSubmit={handleWeatherConfigSubmit}
                                onCancel={() => {
                                    setShowWeatherConfig(false);
                                    onEditingChange(false);
                                }}
                                initialData={modelConfigurations[selectedModel.id]?.weatherConfig}
                            />
                        </div>
                    )}

                    {showParametersConfig && selectedModel && (
                        <div className="parameters-form-wrapper">
                            <div className="parameters-header">
                                <h5>{t("modelselector:messages.configParam")}</h5>
                            </div>
                            <div className="parameters-body">
                                {selectedModel.jsonSchema ? (
                                    <AutoForm
                                        schema={schemaBridge}
                                        onSubmit={handleParametersSubmit}
                                        model={modelConfigurations[selectedModel.id]?.parameters || {}}
                                        ref={(ref) => formRef.current = ref}
                                        submitField={() => null}
                                    />
                                ) : (
                                    <Alert variant="info">
                                        {t("modelselector:messages.noAdditionalParametersAlert")}
                                    </Alert>
                                )}
                            </div>
                            <div className="parameters-footer">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowParametersConfig(false);
                                        if (!selectedModel.jsonSchema) {
                                            // Se il modello non ha jsonSchema, notifichiamo che è configurato
                                            updateModelConfigurations();
                                        }
                                        onEditingChange(false);
                                    }}
                                >
                                    {selectedModel.jsonSchema ? 'Cancel' : 'Close'}
                                </Button>
                                {selectedModel.jsonSchema && (
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (formRef.current) {
                                                const model = formRef.current.getModel();
                                                handleParametersSubmit(model);
                                            }
                                        }}
                                    >
                                        {t("modelselector:buttons.confirm")}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal show={showDescription} onHide={() => setShowDescription(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("modelselector:modelDescription")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {<div dangerouslySetInnerHTML={{ __html: selectedDescription }} /> || t("modelselector:messages.noDescriptionAvailable")}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ModelSelector;